import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  Network, 
  Building2, 
  User, 
  Briefcase, 
  CalendarDays, 
  FileText, 
  Gift, 
  Search, 
  ZoomIn, 
  ZoomOut, 
  RefreshCw, 
  X,
  Download
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RelationshipGraphProps {
  initialEntityId?: string;
  initialEntityType?: 'organization' | 'contact' | 'opportunity';
  depth?: number; 
  className?: string;
}

type Entity = {
  id: string;
  type: 'organization' | 'contact' | 'opportunity' | 'activity' | 'document' | 'gift';
  name: string;
  data: any;
};

type Relationship = {
  id: string;
  source: string;
  target: string;
  type: string;
  data?: any;
};

interface GraphData {
  nodes: Entity[];
  edges: Relationship[];
}

export function RelationshipGraph({ 
  initialEntityId, 
  initialEntityType = 'organization',
  depth = 1,
  className
}: RelationshipGraphProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], edges: [] });
  const [focusEntity, setFocusEntity] = useState<{id: string, type: string} | null>(initialEntityId && initialEntityType ? {id: initialEntityId, type: initialEntityType} : null);
  const [graphDepth, setGraphDepth] = useState(depth);
  const [searchText, setSearchText] = useState('');
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    showOrganizations: true,
    showContacts: true,
    showOpportunities: true,
    showActivities: false,
    showDocuments: false,
    showGifts: false,
    minimumInteractionCount: 0,
    onlyActiveEntities: true
  });
  
  // View state
  const [viewOptions, setViewOptions] = useState({
    viewMode: 'graph',
    zoomLevel: 1,
    showLabels: true,
    groupByOrganization: true
  });
  
  const graphContainerRef = useRef<HTMLDivElement>(null);
  
  // Load graph data when focus entity changes or depth changes
  useEffect(() => {
    if (focusEntity) {
      loadGraphData(focusEntity.id, focusEntity.type, graphDepth);
    }
  }, [focusEntity, graphDepth]);
  
  // Apply filters when filter state changes
  useEffect(() => {
    if (graphData.nodes.length > 0) {
      applyFilters();
    }
  }, [filters]);
  
  // Load graph data from the database
  const loadGraphData = async (entityId: string, entityType: string, depth: number) => {
    setLoading(true);
    
    try {
      const data = await fetchEntityRelationships(entityId, entityType, depth);
      setGraphData(data);
      setSelectedNodeIds([entityId]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load relationship data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Mock function to fetch entity relationships
  // In a real implementation, this would make complex database queries
  const fetchEntityRelationships = async (
    entityId: string, 
    entityType: string, 
    depth: number
  ): Promise<GraphData> => {
    // This is a simplified implementation
    // In a real-world scenario, you'd write recursive queries or stored procedures
    
    const nodes: Entity[] = [];
    const edges: Relationship[] = [];
    const processedNodes = new Set<string>();
    
    // Helper function to process an entity and its relationships
    const processEntity = async (id: string, type: string, currentDepth: number) => {
      if (processedNodes.has(`${type}-${id}`) || currentDepth > depth) {
        return;
      }
      
      processedNodes.add(`${type}-${id}`);
      
      let entity;
      
      // Fetch the entity
      switch (type) {
        case 'organization':
          const { data: org } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', id)
            .single();
            
          if (org) {
            entity = {
              id,
              type: 'organization',
              name: org.name,
              data: org
            };
            nodes.push(entity);
            
            // Fetch related contacts
            const { data: contacts } = await supabase
              .from('contacts')
              .select('*')
              .eq('organization_id', id);
              
            if (contacts) {
              for (const contact of contacts) {
                if (!processedNodes.has(`contact-${contact.id}`)) {
                  nodes.push({
                    id: contact.id,
                    type: 'contact',
                    name: `${contact.first_name} ${contact.last_name}`,
                    data: contact
                  });
                  
                  edges.push({
                    id: `org-${id}-contact-${contact.id}`,
                    source: id,
                    target: contact.id,
                    type: 'has_contact'
                  });
                  
                  if (currentDepth < depth) {
                    await processEntity(contact.id, 'contact', currentDepth + 1);
                  }
                }
              }
            }
            
            // Fetch opportunities
            const { data: opportunities } = await supabase
              .from('opportunities')
              .select('*')
              .eq('organization_id', id);
              
            if (opportunities) {
              for (const opportunity of opportunities) {
                if (!processedNodes.has(`opportunity-${opportunity.id}`)) {
                  nodes.push({
                    id: opportunity.id,
                    type: 'opportunity',
                    name: opportunity.name,
                    data: opportunity
                  });
                  
                  edges.push({
                    id: `org-${id}-opportunity-${opportunity.id}`,
                    source: id,
                    target: opportunity.id,
                    type: 'has_opportunity'
                  });
                  
                  if (currentDepth < depth) {
                    await processEntity(opportunity.id, 'opportunity', currentDepth + 1);
                  }
                }
              }
            }
            
            // If we want to include parent organization
            if (org.parent_id && currentDepth < depth) {
              edges.push({
                id: `org-${org.parent_id}-parent-${id}`,
                source: org.parent_id,
                target: id,
                type: 'parent_of'
              });
              
              await processEntity(org.parent_id, 'organization', currentDepth + 1);
            }
          }
          break;
          
        case 'contact':
          const { data: contact } = await supabase
            .from('contacts')
            .select('*')
            .eq('id', id)
            .single();
            
          if (contact) {
            entity = {
              id,
              type: 'contact',
              name: `${contact.first_name} ${contact.last_name}`,
              data: contact
            };
            nodes.push(entity);
            
            // Fetch activities
            if (filters.showActivities) {
              const { data: activities } = await supabase
                .from('activities')
                .select('*')
                .eq('contact_id', id);
                
              if (activities) {
                for (const activity of activities) {
                  if (!processedNodes.has(`activity-${activity.id}`)) {
                    nodes.push({
                      id: activity.id,
                      type: 'activity',
                      name: activity.title,
                      data: activity
                    });
                    
                    edges.push({
                      id: `contact-${id}-activity-${activity.id}`,
                      source: id,
                      target: activity.id,
                      type: 'has_activity'
                    });
                  }
                }
              }
            }
            
            // Fetch organization if not already processed
            if (contact.organization_id && currentDepth < depth) {
              edges.push({
                id: `org-${contact.organization_id}-contact-${id}`,
                source: contact.organization_id,
                target: id,
                type: 'has_contact'
              });
              
              await processEntity(contact.organization_id, 'organization', currentDepth + 1);
            }
            
            // Additional relations could be added here
          }
          break;
          
        case 'opportunity':
          const { data: opportunity } = await supabase
            .from('opportunities')
            .select('*')
            .eq('id', id)
            .single();
            
          if (opportunity) {
            entity = {
              id,
              type: 'opportunity',
              name: opportunity.name,
              data: opportunity
            };
            nodes.push(entity);
            
            // Fetch related organization if not already processed
            if (opportunity.organization_id && currentDepth < depth) {
              edges.push({
                id: `org-${opportunity.organization_id}-opportunity-${id}`,
                source: opportunity.organization_id,
                target: id,
                type: 'has_opportunity'
              });
              
              await processEntity(opportunity.organization_id, 'organization', currentDepth + 1);
            }
            
            // Fetch related contacts (assuming there's a junction table)
            const { data: opportunityContacts } = await supabase
              .from('opportunity_contacts')
              .select('contact_id')
              .eq('opportunity_id', id);
              
            if (opportunityContacts) {
              for (const contactRel of opportunityContacts) {
                if (currentDepth < depth) {
                  edges.push({
                    id: `opportunity-${id}-contact-${contactRel.contact_id}`,
                    source: id,
                    target: contactRel.contact_id,
                    type: 'involves_contact'
                  });
                  
                  await processEntity(contactRel.contact_id, 'contact', currentDepth + 1);
                }
              }
            }
          }
          break;
          
        // Add other entity types as needed
      }
    };
    
    // Start processing from the initial entity
    await processEntity(entityId, entityType, 0);
    
    return { nodes, edges };
  };
  
  // Apply filters to the graph data
  const applyFilters = () => {
    // In a real implementation, you'd filter the graph data based on the current filters
    // For now, we'll just simulate this by not doing anything
    // You could implement this by keeping a full copy of the graph data
    // and filtering it when needed
    
    // For example:
    /*
    const filteredNodes = fullGraphData.nodes.filter(node => {
      // Apply entity type filters
      if (node.type === 'organization' && !filters.showOrganizations) return false;
      if (node.type === 'contact' && !filters.showContacts) return false;
      // etc.
      
      // Apply active filter
      if (filters.onlyActiveEntities && node.data.status !== 'active') return false;
      
      return true;
    });
    
    const filteredEdges = fullGraphData.edges.filter(edge => {
      const sourceNode = filteredNodes.find(n => n.id === edge.source);
      const targetNode = filteredNodes.find(n => n.id === edge.target);
      return sourceNode && targetNode;
    });
    
    setGraphData({ nodes: filteredNodes, edges: filteredEdges });
    */
  };
  
  // Handle node selection
  const handleNodeSelect = (nodeId: string) => {
    const node = graphData.nodes.find(n => n.id === nodeId);
    if (node) {
      setSelectedNodeIds([nodeId]);
    }
  };
  
  // Handle node navigation
  const handleNodeNavigation = (node: Entity) => {
    switch (node.type) {
      case 'organization':
        navigate(`/organizations/${node.id}`);
        break;
      case 'contact':
        navigate(`/contacts/${node.id}`);
        break;
      case 'opportunity':
        navigate(`/opportunities/${node.id}`);
        break;
      case 'activity':
        navigate(`/activities/${node.id}`);
        break;
      // Add cases for other entity types
    }
  };
  
  const handleExport = () => {
    // In a real implementation, you'd generate and download graph data
    // in a format like JSON, CSV, or an image/SVG of the visualization
    toast({
      title: "Export",
      description: "Relationship graph exported successfully!",
    });
  };
  
  // Render graph visualization (simplified for this example)
  const renderGraph = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading relationship data...</span>
        </div>
      );
    }
    
    if (graphData.nodes.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
          <Network className="h-12 w-12 mb-2" />
          <p>No relationship data available</p>
          <Button className="mt-4" onClick={() => loadGraphData(initialEntityId || '', initialEntityType, graphDepth)}>
            Load Data
          </Button>
        </div>
      );
    }
    
    // Render nodes and edges (in a real implementation, you'd use a proper graph visualization library)
    return (
      <div className="relative h-[400px] bg-muted/20 rounded-md flex flex-col items-center justify-center overflow-hidden">
        <div className="text-center text-muted-foreground">
          <Network className="h-12 w-12 mx-auto mb-2" />
          <p>
            This is a placeholder for a graph visualization. In a real implementation, you would integrate 
            a library like <strong>Force Graph</strong>, <strong>D3.js</strong>, <strong>Cytoscape.js</strong>,
            or <strong>React Flow</strong> to render the relationship graph.
          </p>
        </div>
        
        <div className="mt-6">
          <p className="text-muted-foreground text-sm mb-2">
            Graph contains:
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Badge type="organization" count={graphData.nodes.filter(n => n.type === 'organization').length} />
            <Badge type="contact" count={graphData.nodes.filter(n => n.type === 'contact').length} />
            <Badge type="opportunity" count={graphData.nodes.filter(n => n.type === 'opportunity').length} />
            {filters.showActivities && (
              <Badge type="activity" count={graphData.nodes.filter(n => n.type === 'activity').length} />
            )}
            {filters.showDocuments && (
              <Badge type="document" count={graphData.nodes.filter(n => n.type === 'document').length} />
            )}
            {filters.showGifts && (
              <Badge type="gift" count={graphData.nodes.filter(n => n.type === 'gift').length} />
            )}
          </div>
          <p className="text-muted-foreground text-sm mt-2">
            <span className="font-medium">{graphData.edges.length}</span> connections
          </p>
        </div>
      </div>
    );
  };
  
  // Render the entity list
  const renderEntityList = () => {
    return (
      <div className="h-[400px] overflow-y-auto border rounded-md divide-y">
        {graphData.nodes.map(node => (
          <div 
            key={node.id} 
            className={`p-2 hover:bg-muted cursor-pointer ${
              selectedNodeIds.includes(node.id) ? 'bg-muted' : ''
            }`}
            onClick={() => handleNodeSelect(node.id)}
            onDoubleClick={() => handleNodeNavigation(node)}
          >
            <div className="flex items-center">
              <EntityIcon type={node.type} className="mr-2" />
              <div>
                <p className="font-medium">{node.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{node.type}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Selected node details
  const renderSelectedNodeDetails = () => {
    if (selectedNodeIds.length === 0) return null;
    
    const node = graphData.nodes.find(n => n.id === selectedNodeIds[0]);
    if (!node) return null;
    
    return (
      <div className="mt-4 p-4 border rounded-md bg-muted/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <EntityIcon type={node.type} className="mr-2" />
            <h3 className="font-medium">{node.name}</h3>
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => handleNodeNavigation(node)}
          >
            View Details
          </Button>
        </div>
        
        <div className="text-sm space-y-1">
          {node.type === 'organization' && (
            <>
              <p><span className="text-muted-foreground">Industry:</span> {node.data.industry || 'N/A'}</p>
              <p><span className="text-muted-foreground">Status:</span> {node.data.status || 'N/A'}</p>
              <p><span className="text-muted-foreground">Tier:</span> {node.data.tier || 'N/A'}</p>
            </>
          )}
          
          {node.type === 'contact' && (
            <>
              <p><span className="text-muted-foreground">Job Title:</span> {node.data.job_title || 'N/A'}</p>
              <p><span className="text-muted-foreground">Email:</span> {node.data.email || 'N/A'}</p>
              <p><span className="text-muted-foreground">Phone:</span> {node.data.phone || 'N/A'}</p>
            </>
          )}
          
          {node.type === 'opportunity' && (
            <>
              <p><span className="text-muted-foreground">Value:</span> ${node.data.value?.toLocaleString() || '0'}</p>
              <p><span className="text-muted-foreground">Stage:</span> {node.data.stage || 'N/A'}</p>
              <p><span className="text-muted-foreground">Probability:</span> {node.data.probability || '0'}%</p>
            </>
          )}
          
          {/* Add more entity-specific fields here */}
        </div>
        
        {/* Connected entities */}
        <div className="mt-3">
          <h4 className="text-sm font-medium mb-1">Connected Entities</h4>
          <div className="max-h-[200px] overflow-y-auto">
            {graphData.edges
              .filter(edge => edge.source === node.id || edge.target === node.id)
              .map(edge => {
                const connectedNodeId = edge.source === node.id ? edge.target : edge.source;
                const connectedNode = graphData.nodes.find(n => n.id === connectedNodeId);
                
                if (!connectedNode) return null;
                
                return (
                  <div 
                    key={edge.id} 
                    className="flex items-center text-sm p-1 hover:bg-muted rounded cursor-pointer"
                    onClick={() => handleNodeSelect(connectedNodeId)}
                  >
                    <EntityIcon type={connectedNode.type} size={14} className="mr-1.5" />
                    <span>{connectedNode.name}</span>
                    <span className="ml-1.5 text-xs text-muted-foreground capitalize">
                      ({edge.source === node.id ? 'to' : 'from'})
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Network className="h-5 w-5 mr-2" />
              Relationship Explorer
            </CardTitle>
            <CardDescription>
              Visualize connections between entities
            </CardDescription>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button 
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <X className="h-4 w-4 mr-1" /> : <Search className="h-4 w-4 mr-1" />}
              {showFilters ? "Hide Filters" : "Filters"}
            </Button>
            
            <Button
              size="sm"
              variant="outline"
              disabled={loading || graphData.nodes.length === 0}
              onClick={handleExport}
            >
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Entity search and focus controls */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-grow">
            {focusEntity ? (
              <div className="flex items-center">
                <Label className="mr-2">Focus Entity:</Label>
                <div className="flex items-center rounded border p-2 flex-grow">
                  <EntityIcon type={focusEntity.type} className="mr-2" />
                  <span className="flex-grow truncate">
                    {graphData.nodes.find(n => n.id === focusEntity.id)?.name || "Unknown Entity"}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setFocusEntity(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Label>Search for an entity to explore</Label>
                <div className="flex">
                  <input 
                    type="text" 
                    placeholder="Search by name..."
                    className="flex-grow p-2 rounded-l border focus:outline-none focus:ring-2 focus:ring-primary"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Button className="rounded-l-none">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                {/* In a real implementation, you'd show search results here */}
              </div>
            )}
          </div>
          
          <div className="flex gap-2 items-end">
            <div>
              <Label className="text-xs">Depth</Label>
              <Select
                value={graphDepth.toString()}
                onValueChange={(value) => setGraphDepth(parseInt(value))}
              >
                <SelectTrigger className="w-24">
                  <SelectValue placeholder="Depth" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Level</SelectItem>
                  <SelectItem value="2">2 Levels</SelectItem>
                  <SelectItem value="3">3 Levels</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => loadGraphData(
                focusEntity?.id || initialEntityId || '',
                focusEntity?.type || initialEntityType,
                graphDepth
              )}
              disabled={loading || !focusEntity}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        {showFilters && (
          <div className="p-4 border rounded-md space-y-4">
            <h3 className="font-medium">Filters</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-orgs" 
                  checked={filters.showOrganizations}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, showOrganizations: !!checked})
                  }
                />
                <Label htmlFor="show-orgs" className="text-sm">Organizations</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-contacts" 
                  checked={filters.showContacts}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, showContacts: !!checked})
                  }
                />
                <Label htmlFor="show-contacts" className="text-sm">Contacts</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-opps" 
                  checked={filters.showOpportunities}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, showOpportunities: !!checked})
                  }
                />
                <Label htmlFor="show-opps" className="text-sm">Opportunities</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-activities" 
                  checked={filters.showActivities}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, showActivities: !!checked})
                  }
                />
                <Label htmlFor="show-activities" className="text-sm">Activities</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-documents" 
                  checked={filters.showDocuments}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, showDocuments: !!checked})
                  }
                />
                <Label htmlFor="show-documents" className="text-sm">Documents</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-gifts" 
                  checked={filters.showGifts}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, showGifts: !!checked})
                  }
                />
                <Label htmlFor="show-gifts" className="text-sm">Gifts</Label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="interaction-count" className="text-sm">
                    Min. Interaction Count
                  </Label>
                  <span className="text-sm">{filters.minimumInteractionCount}</span>
                </div>
                <Slider
                  id="interaction-count"
                  min={0}
                  max={10}
                  step={1}
                  value={[filters.minimumInteractionCount]}
                  onValueChange={(values) => 
                    setFilters({...filters, minimumInteractionCount: values[0]})
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="active-only" className="text-sm">Show Active Entities Only</Label>
                <Switch
                  id="active-only"
                  checked={filters.onlyActiveEntities}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, onlyActiveEntities: checked})
                  }
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilters({
                    showOrganizations: true,
                    showContacts: true,
                    showOpportunities: true,
                    showActivities: false,
                    showDocuments: false,
                    showGifts: false,
                    minimumInteractionCount: 0,
                    onlyActiveEntities: true
                  });
                }}
              >
                Reset Filters
              </Button>
            </div>
          </div>
        )}
        
        {/* View options */}
        <div className="flex justify-between items-center">
          <Tabs
            defaultValue={viewOptions.viewMode}
            onValueChange={(value) => setViewOptions({...viewOptions, viewMode: value})}
          >
            <TabsList>
              <TabsTrigger value="graph">Graph</TabsTrigger>
              <TabsTrigger value="list">List</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setViewOptions({...viewOptions, zoomLevel: Math.max(0.5, viewOptions.zoomLevel - 0.1)})}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setViewOptions({...viewOptions, zoomLevel: Math.min(2, viewOptions.zoomLevel + 0.1)})}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center space-x-2">
              <Label htmlFor="show-labels" className="text-sm">Labels</Label>
              <Switch
                id="show-labels"
                checked={viewOptions.showLabels}
                onCheckedChange={(checked) => 
                  setViewOptions({...viewOptions, showLabels: checked})
                }
              />
            </div>
          </div>
        </div>
        
        {/* Main visualization area */}
        <div ref={graphContainerRef} className="border rounded">
          {viewOptions.viewMode === 'graph' ? renderGraph() : renderEntityList()}
        </div>
        
        {/* Selected node details */}
        {selectedNodeIds.length > 0 && renderSelectedNodeDetails()}
      </CardContent>
    </Card>
  );
}

// Helper components

const EntityIcon = ({ type, size = 16, className = '' }) => {
  switch (type) {
    case 'organization':
      return <Building2 className={className} style={{ width: size, height: size }} />;
    case 'contact':
      return <User className={className} style={{ width: size, height: size }} />;
    case 'opportunity':
      return <Briefcase className={className} style={{ width: size, height: size }} />;
    case 'activity':
      return <CalendarDays className={className} style={{ width: size, height: size }} />;
    case 'document':
      return <FileText className={className} style={{ width: size, height: size }} />;
    case 'gift':
      return <Gift className={className} style={{ width: size, height: size }} />;
    default:
      return null;
  }
};

const Badge = ({ type, count }) => {
  const getBgColor = () => {
    switch (type) {
      case 'organization': return 'bg-blue-100 text-blue-800';
      case 'contact': return 'bg-green-100 text-green-800';
      case 'opportunity': return 'bg-purple-100 text-purple-800';
      case 'activity': return 'bg-orange-100 text-orange-800';
      case 'document': return 'bg-yellow-100 text-yellow-800';
      case 'gift': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div className={`flex items-center px-2 py-0.5 rounded-full text-xs ${getBgColor()}`}>
      <EntityIcon type={type} size={12} className="mr-1" />
      <span className="capitalize">{type}s:</span>
      <span className="font-medium ml-1">{count}</span>
    </div>
  );
};